import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
} from '@mui/material';
import {
  Person,
  Groups,
  DirectionsBus,
  CalendarMonth,
  Schedule,
  LocationOn,
  ArrowBack,
  CheckCircle,
  Home,
  Lock,
  VpnKey,
  Dashboard,
  EventNote,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import hfssLogo from '../../assets/hfss_logo.png';

interface Passenger {
  display_name: string;
  seats: number;
  is_member: boolean;
  member_status: 'member' | 'visiting_member' | 'guest';
}

interface ShuttleInfo {
  name: string;
  capacity: number;
}

interface ScheduleInfo {
  id: string;
  departure_date: string;
  departure_time: string;
  pickup_location: string;
  destination: string;
}

interface ClubInfo {
  name: string;
}

interface PassengerListData {
  schedule: ScheduleInfo;
  shuttle: ShuttleInfo;
  club: ClubInfo;
  passengers: Passenger[];
  total_booked_seats: number;
  available_seats: number;
}

const ShuttlePassengersPage: React.FC = () => {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<PassengerListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(true);
  const [pinError, setPinError] = useState<string | null>(null);
  const [identifier, setIdentifier] = useState('');
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Check if user is logged in as pilot
  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isPilot = user?.is_pilot;

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  
  // Get the locale for date formatting
  const dateLocale = i18n.language === 'it' ? it : enUS;

  const fetchPassengers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/public/schedules/${scheduleId}/passengers`);
      
      if (!response.ok) {
        throw new Error(t('shuttle.errors.fetchPassengers'));
      }
      
      const result = await response.json();
      setData(result);
    } catch (error: any) {
      console.error('Failed to fetch passengers:', error);
      setError(error.message || t('shuttle.errors.fetchPassengers'));
    } finally {
      setLoading(false);
    }
  }, [scheduleId, t, apiUrl]);

  useEffect(() => {
    // Check if user is a logged-in pilot - they don't need PIN
    if (isAuthenticated && isPilot) {
      setAuthenticated(true);
      setShowPinDialog(false);
      fetchPassengers();
      return;
    }
    
    // Check if already authenticated in this session
    const authKey = `passenger_auth_${scheduleId}`;
    const existingAuth = sessionStorage.getItem(authKey);
    if (existingAuth) {
      setAuthenticated(true);
      setShowPinDialog(false);
      fetchPassengers();
    }
  }, [scheduleId, fetchPassengers, isAuthenticated, isPilot]);

  const verifyPin = async () => {
    if (!identifier || !pin || pin.length !== 4) {
      setPinError(t('shuttle.passengers.enterCredentials'));
      return;
    }

    setVerifying(true);
    setPinError(null);

    try {
      const response = await fetch(`${apiUrl}/public/schedules/${scheduleId}/verify-passenger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: identifier,
          pin_code: pin,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('shuttle.passengers.invalidCredentials'));
      }

      const result = await response.json();
      
      // Store authentication in session
      const authKey = `passenger_auth_${scheduleId}`;
      sessionStorage.setItem(authKey, JSON.stringify({
        pilot: result.pilot,
        booking: result.booking,
        timestamp: new Date().toISOString(),
      }));

      setAuthenticated(true);
      setShowPinDialog(false);
      fetchPassengers();
    } catch (error: any) {
      console.error('PIN verification failed:', error);
      setPinError(error.message || t('shuttle.passengers.invalidCredentials'));
    } finally {
      setVerifying(false);
    }
  };

  // Show PIN dialog if not authenticated
  if (showPinDialog && !authenticated) {
    return (
      <>
        <AppBar position="static" sx={{ background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate('/')}
              sx={{ mr: 2 }}
            >
              <Home />
            </IconButton>
            <Box
              component="img"
              src={hfssLogo}
              alt="HFSS"
              sx={{ height: 40, mr: 2, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {t('shuttle.passengers.title')}
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="sm" sx={{ mt: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Lock sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {t('shuttle.passengers.authRequired')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('shuttle.passengers.authDescription')}
              </Typography>
            </Box>

            {pinError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPinError(null)}>
                {pinError}
              </Alert>
            )}

            <TextField
              fullWidth
              label={t('shuttle.passengers.emailOrNickname')}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              margin="normal"
              autoFocus
            />

            <TextField
              fullWidth
              label={t('shuttle.passengers.pin')}
              type="password"
              value={pin}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,4}$/.test(value)) {
                  setPin(value);
                }
              }}
              margin="normal"
              inputProps={{ maxLength: 4 }}
              helperText={t('shuttle.passengers.pinHelp')}
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/app/shuttle-booking')}
              >
                {t('common.back')}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={verifyPin}
                disabled={!identifier || pin.length !== 4 || verifying}
                startIcon={verifying ? <CircularProgress size={20} /> : <VpnKey />}
              >
                {verifying ? t('common.verifying') : t('common.verify')}
              </Button>
            </Box>
          </Paper>
        </Container>
      </>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('shuttle.errors.dataNotFound')}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          {t('common.back')}
        </Button>
      </Container>
    );
  }

  const occupancyRate = (data.total_booked_seats / data.shuttle.capacity) * 100;
  const departureDate = new Date(data.schedule.departure_date);

  return (
    <>
      {/* Only show header for non-authenticated users (public access) */}
      {!isAuthenticated && !isPilot && (
        <AppBar position="static" sx={{ background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate('/')}
              sx={{ mr: 2 }}
            >
              <Home />
            </IconButton>
            <Box
              component="img"
              src={hfssLogo}
              alt="HFSS"
              sx={{ height: 40, mr: 2, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {t('shuttle.passengers.title')}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Container maxWidth="md" sx={{ mt: (isAuthenticated || isPilot) ? 4 : 4, mb: 4 }}>
        {/* Navigation buttons for authenticated users */}
        {(isAuthenticated || isPilot) && (
          <Box sx={{ 
            mb: 2, 
            display: 'flex', 
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            <Button
              variant="outlined"
              startIcon={<EventNote />}
              onClick={() => navigate('/app/shuttle-booking')}
              size="small"
            >
              {i18n.language === 'it' ? 'Le Mie Prenotazioni' : 'My Bookings'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Dashboard />}
              onClick={() => navigate('/app/pilot-dashboard')}
              size="small"
            >
              {i18n.language === 'it' ? 'Dashboard Pilota' : 'Pilot Dashboard'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              size="small"
            >
              {i18n.language === 'it' ? 'Indietro' : 'Back'}
            </Button>
          </Box>
        )}

        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>

        {/* Shuttle Info Card */}
        <Card sx={{ mb: 3, backgroundColor: 'primary.50' }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DirectionsBus sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    {data.shuttle.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {data.club.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarMonth sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {format(departureDate, 'EEEE, d MMMM yyyy', { locale: dateLocale })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Schedule sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {data.schedule.departure_time}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">
                      {t('shuttle.from')}: {data.schedule.pickup_location || t('shuttle.defaultPickup')}
                    </Typography>
                    <Typography variant="body2">
                      {t('shuttle.to')}: {data.schedule.destination || t('shuttle.defaultDestination')}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {t('shuttle.occupancy')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {data.total_booked_seats} / {data.shuttle.capacity}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={occupancyRate}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'grey.300',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: occupancyRate > 80 ? 'warning.main' : 'success.main',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Show pilot authentication note */}
        {isPilot && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Authenticated as pilot - Full access granted
            </Typography>
          </Alert>
        )}

        {/* Passenger List */}
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Groups sx={{ mr: 1 }} />
          {t('shuttle.passengers.list')} ({data.passengers.length})
        </Typography>

        {data.passengers.length === 0 ? (
          <Alert severity="info">
            {t('shuttle.passengers.noPassengers')}
          </Alert>
        ) : (
          <List>
            {data.passengers.map((passenger, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ backgroundColor: passenger.is_member ? 'primary.main' : 'grey.500' }}>
                      <Person />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={passenger.display_name}
                    secondary={
                      passenger.seats > 1
                        ? `${passenger.seats} ${t('shuttle.passengers.seats')}`
                        : `1 ${t('shuttle.passengers.seat')}`
                    }
                  />
                  {passenger.member_status === 'member' && (
                    <Chip
                      label={t('shuttle.passengers.member')}
                      size="small"
                      color="primary"
                      variant="outlined"
                      icon={<CheckCircle />}
                    />
                  )}
                  {passenger.member_status === 'visiting_member' && (
                    <Chip
                      label={t('shuttle.passengers.visitingMember')}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      icon={<CheckCircle />}
                    />
                  )}
                  {passenger.member_status === 'guest' && (
                    <Chip
                      label={t('shuttle.passengers.guest')}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                </ListItem>
                {index < data.passengers.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Available Seats Alert */}
        {data.available_seats > 0 && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {t('shuttle.passengers.availableSeats', { count: data.available_seats })}
          </Alert>
        )}
        
        {data.available_seats === 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {t('shuttle.passengers.fullBooked')}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/app/shuttle-booking')}
          >
            {t('shuttle.bookNow')}
          </Button>
        </Box>
        </Paper>
      </Container>
    </>
  );
};

export default ShuttlePassengersPage;